/* global zxcvbn */

import React, { useState, useEffect, useRef, FormEvent } from 'react';
import { connect } from 'react-redux';
import { useLocation } from 'react-router';
import queryString from 'querystring';
import { css, useTheme } from '@emotion/react';
import { BirdiTheme, breakpoints } from '@birdi/theme';
import BirdiLogoSVG from '@birdi/icons/birdi-logo-navy.svg';
import EmailIcon from '@birdi/icons/onboarding-at.svg';
import LockIcon from '@birdi/icons/onboarding-lock.svg';
import ErrorDisplay from '../../common/ErrorDisplay';
import {
  updateField,
  resetPassword,
  sendPasswordResetCodeToEmail,
} from '../actions';
import EnterCode from '../EnterCode';
import { getZxcvbnResult } from '../resetpasswordutils';
import * as styles from './styles';

interface ResetPasswordProps {
  auth: any;
  _updateField: (field: any) => void;
  _sendPasswordResetCodeToEmail: (email: string) => void;
  _resetPassword: (obj: any) => void;
}

const ResetPassword: React.FC<ResetPasswordProps> = (props) => {
  const theme = useTheme() as BirdiTheme;
  const location = useLocation();
  const [password, setPassword] = useState('');
  const [zxcvbnResult, setZxcvbnResult] = useState<any>(null);
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    return () => {
      setPassword('');
      props._updateField({
        pendingCode: false,
        code: '',
        error: null,
      });
    };
  }, []);

  const handleSetPassword = (value: string) => {
    const { auth } = props;
    if (typeof (window as any).zxcvbn === 'function') {
      const zxcvbnResultValue = getZxcvbnResult(value, auth.email, auth.name);
      setZxcvbnResult(zxcvbnResultValue);
    }
    setPassword(value);
  };

  const handleEmailSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const { _sendPasswordResetCodeToEmail, auth } = props;
    if (!formRef.current?.checkValidity()) {
      return formRef.current?.reportValidity();
    }
    return _sendPasswordResetCodeToEmail(auth.email);
  };

  const handlePasswordReset = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const { auth, _resetPassword } = props;
    const newSearch = location.search.replace('?', '');
    const queryObject = queryString.parse(newSearch);
    const { id } = queryObject;
    const code = queryObject.code ? queryObject.code : auth.code;
    if (!formRef.current?.checkValidity()) {
      return formRef.current?.reportValidity();
    }
    return _resetPassword({
      id: auth.codeId,
      code,
      password,
    });
  };

  const { auth, _updateField } = props;

  // Initial state: Enter email
  if (!auth.pendingCode && !location.search) {
    return (
      <>
        {/* Logo */}
        <div css={styles.logoContainer}>
          <BirdiLogoSVG />
        </div>

        {/* Header Section */}
        <div css={styles.headerSection}>
          <h1 css={styles.title}>Reset Password</h1>
          <p
            css={(theme: BirdiTheme) => css`
              ${styles.subtitle(theme)} margin-bottom: 1.5em;
            `}
          >
            Enter your email address below and we'll send you a code to reset
            your password.
          </p>
        </div>

        {/* Form */}
        <form
          ref={formRef}
          onSubmit={handleEmailSubmit}
          css={styles.formContainer}
        >
          <div css={styles.formField}>
            <label css={styles.label} htmlFor="email">
              Email address <span className="required">*</span>
            </label>
            <div css={styles.inputWrapper}>
              <div css={styles.inputIcon}>
                <EmailIcon />
              </div>
              <input
                id="email"
                type="email"
                css={styles.input}
                placeholder="Enter your email"
                value={auth.email}
                onChange={(event) =>
                  _updateField({ email: event.target.value })
                }
                required
              />
            </div>
            <ErrorDisplay error={auth.error} path="email" />
          </div>

          {/* Button */}
          <div css={styles.buttonContainer}>
            <button type="submit" css={styles.primaryButton}>
              Reset Password
            </button>
          </div>
          <ErrorDisplay error={auth.error} />
        </form>

        {/* Help Link */}
        <div css={styles.helpLink}>
          Need help? <a href="mailto:support@birdi.io">Contact support</a>
        </div>
      </>
    );
  }

  // State 2: Enter code and new password
  return (
    <>
      {/* Logo - Fixed at top */}
      <div css={styles.logoContainer}>
        <BirdiLogoSVG />
      </div>

      {/* Scrollable Content */}
      <div css={styles.scrollableContent}>
        {/* Header Section */}
        <div css={styles.headerSection}>
          <h1 css={styles.title}>Reset Password</h1>
          {!location.search && (
            <p css={styles.subtitle}>
              We've sent an email to {auth.email}. Enter the code below and your
              new password.
            </p>
          )}
          {location.search && (
            <p css={styles.subtitle}>Enter your new password.</p>
          )}
        </div>

        {/* Form - EnterCode component handles its own form */}
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 0.25rem;

            @media (min-width: ${breakpoints.s}) {
              gap: 0.5rem;
            }
          `}
        >
          <EnterCode
            form={formRef}
            auth={auth}
            _updateField={_updateField}
            onSubmit={handlePasswordReset}
            setPassword={handleSetPassword}
            password={password}
            zxcvbnResult={zxcvbnResult}
            search={location.search}
            label="Reset Password"
          />
        </div>
      </div>

      {/* Fixed Footer - Help Link */}
      <div css={styles.fixedFooter}>
        <div css={styles.helpLink}>
          Need help? <a href="mailto:support@birdi.io">Contact support</a>
        </div>
      </div>
    </>
  );
};

export default connect(
  (state: any) => ({
    auth: state.auth,
  }),
  (dispatch: any) => ({
    _updateField: (field: any) => dispatch(updateField(field)),
    _sendPasswordResetCodeToEmail: (email: string) =>
      dispatch(sendPasswordResetCodeToEmail(email)),
    _resetPassword: (obj: any) => dispatch(resetPassword(obj)),
  }),
)(ResetPassword);
